import { motion } from 'framer-motion';
import React from 'react';

import { cn } from './cn';

export interface SimpleTableColumn<T = any> {
  key: string;
  name: string;
  fieldName?: string;
  minWidth?: number;
  maxWidth?: number;
  onRender?: (item: T) => React.ReactNode;
}

export interface SimpleTableProps<T = any> {
  columns: SimpleTableColumn<T>[];
  items: T[];
  maxHeight?: number;
  className?: string;
  onRowClick?: (item: T) => void;
  /** Rendered under the rows, inside the scrolling box, so a paging footer is reached by scrolling the rows. */
  footer?: React.ReactNode;
}

export function SimpleTable<T extends Record<string, any>>({
  columns,
  items,
  maxHeight,
  className,
  onRowClick,
  footer,
}: SimpleTableProps<T>) {
  return (
    <div className={cn('w-full overflow-hidden rounded-lg border border-border', className)}>
      <div className="overflow-x-auto" style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}>
        <table className="w-full text-left type-body-sm">
          <thead className="sticky top-0 z-10 border-b border-border bg-surface-raised">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-2.5 type-table-header text-text-muted"
                  style={{
                    minWidth: col.minWidth,
                    maxWidth: col.maxWidth,
                  }}
                >
                  {col.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item, idx) => (
              <motion.tr
                key={idx}
                className={cn(
                  'bg-surface transition-colors duration-150',
                  onRowClick ? 'cursor-pointer hover:bg-gray-50' : 'hover:bg-gray-50/50'
                )}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15, delay: idx * 0.02 }}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className="px-4 py-3 type-body-sm text-text-primary"
                    style={{
                      minWidth: col.minWidth,
                      maxWidth: col.maxWidth,
                    }}
                  >
                    {col.onRender ? col.onRender(item) : col.fieldName ? String(item[col.fieldName] ?? '') : ''}
                  </td>
                ))}
              </motion.tr>
            ))}
          </tbody>
        </table>
        {footer}
      </div>
    </div>
  );
}
